// Transient effects for the fast client: explosions when a fleet goes down,
// small rings for cell captures and hits, and the detonation look of a round
// that goes off (ui-map-interface.md §6.3, §6.3.5).
//
// It is `render/fx.ts` with the per-frame `Graphics` taken out. Effects are
// the only fully saturated thing on the map and they never last more than
// 1.5 s, so the layer holds a short list of live items with an age and
// packs one circle batch from it: the flashes are discs, the blast fronts
// are rings, and a bubble is a tiny stroked ring, which is what the PixiJS
// layer strokes them as.
//
// Every figure below is the PixiJS layer's own. They are ages, radii and
// ramps of one animation rather than tuning a designer moves, so they live
// in the layer in both clients.
//
// A round that goes off draws the DETONATION LOOK the World theme names
// (R2079): one of the ten cards of mockup round 065, out to the splash of
// the round's kind. The look draws on three batches of its own, flushed
// before the circle batch of the other effects: the gradients and the
// filled shapes, then the strokes, then the circles.

import { factionColor } from '../../world';
import { FOAM_INK } from '../../render/combat-recipe-kit';
import {
  DET_LIFE_SEC, DETONATION_AUTO, detonationLook, formOf, powerOf, roundIdOf, seedOf,
  type DetonationCtx, type DetonationForm, type DetonationLook, type DetonationRound,
} from '../../render/detonation-styles';
import { TORPEDO_DETONATION } from '../../gen/balance';
import { F_ROUND, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { BatchSource, CircleSink, QuadSink, SegmentSink } from './batches';
import { holdScale, zoomOf } from './shared';

type FxKind = 'boom' | 'ring' | 'hit' | 'launch' | 'slow';

interface Bubble { dx: number; rise: number; phase: number }

interface Fx {
  kind: FxKind;
  x: number;
  y: number;
  age: number;
  life: number;
  color: number;
  /**
   * Multiplies every radius and stroke of the blast. 1 is the baseline; the
   * brand art scales a blast with it. A round that goes off draws the
   * detonation look, which no style scales (R2079). The slow ring keeps
   * its radius here.
   */
  scale: number;
  bubbles: readonly Bubble[];
}

/** What an effect with no bubbles holds. It is shared and never written. */
const NO_BUBBLES: readonly Bubble[] = [];

/** One round that went off: its point, its age and the round as the look
 *  reads it. */
interface Det {
  x: number;
  y: number;
  age: number;
  ink: number;
  R: number;
  power: number;
  form: DetonationForm;
  seed: number;
  /** The course of the round: the bearing from its launch point to the
   *  impact, NaN when the round names no launch point (R2842). */
  course: number;
}

/**
 * The splash and the damage of a kind off the pack rows (`gen/balance.ts`),
 * for a page with no hello catalog: the reel page, the theme preview and a
 * film with no catalog. A live page hands the catalog in (`setRounds`).
 */
export function packRound(kind: number): DetonationRound | undefined {
  return TORPEDO_DETONATION[roundIdOf(kind)];
}

/** `n` bubbles: a side drift of `spread` px, a rise of `rise0` to `rise0 + riseSpan`. */
function bubblesOf(n: number, spread: number, rise0: number, riseSpan: number): Bubble[] {
  const out: Bubble[] = [];
  for (let i = 0; i < n; i++) {
    out.push({
      dx: (Math.random() - 0.5) * spread,
      rise: rise0 + Math.random() * riseSpan,
      phase: Math.random() * Math.PI * 2,
    });
  }
  return out;
}

/** A launch point nearer the impact than this many world px names no
 *  course (R2842). */
const COURSE_MIN_PX = 0.5;

/** The white core of a flash, and the orange fireball behind the front. */
const FLASH_WHITE = 0xffffff;
const FIREBALL = 0xffa864;

export class FxLayer implements Layer {
  readonly name = 'fx';

  private readonly circles: CircleSink;
  private readonly quads: QuadSink;
  private readonly segs: SegmentSink;
  private readonly items: Fx[] = [];
  private readonly dets: Det[] = [];
  private shown = true;
  /** The detonation look the World theme names, and the eye-test knob that
   *  overrides it (`#detonationlook=<id>`, '' for none). */
  private lookId = DETONATION_AUTO;
  private forced = '';
  private look: DetonationLook = detonationLook(DETONATION_AUTO);
  /** The splash and the damage of a round kind: the hello catalog on a live
   *  page, the pack rows elsewhere. */
  private rounds: (kind: number) => DetonationRound | undefined = packRound;
  /** The one context every detonation reuses (R131). */
  private readonly det: DetonationCtx;
  /**
   * Past this camera zoom every blast holds the screen size it has AT this
   * zoom, in place of growing with the map (R552). 0, the live map's
   * answer, never holds. The replay film sets it, because a film zooms in
   * on a fight and a blast the size of the hull it struck hides the hull.
   */
  holdZoom = 0;
  /** The camera zoom of the frame being packed. */
  private zoom = 1;

  constructor(src: BatchSource) {
    this.quads = src.newQuads(128);
    this.segs = src.newSegments(256);
    this.circles = src.newCircles(256);
    this.det = {
      z: 1, row: this.look.row, round: F_ROUND, x: 0, y: 0, t: 0, R: 0, power: 0,
      form: 'blast', seed: 0, ink: 0, course: NaN,
      quads: this.quads, segs: this.segs, rings: this.circles,
      sprite: (key) => src.sprite(key),
    };
  }

  // -- the detonation look ---------------------------------------------------

  /** The detonation look of the World theme (R2079). */
  setDetonationLook(id: string): void {
    this.lookId = id;
    this.look = detonationLook(this.forced || id);
  }

  /** The eye-test knob `#detonationlook=<id>`: one look in place of the
   *  theme's, '' for none. */
  setDetonationForced(id: string): void {
    this.forced = id;
    this.look = detonationLook(id || this.lookId);
  }

  /** The look that draws now, for a test and for the film that copies it. */
  get detonationId(): string { return this.look.id; }

  /** Where the splash and the damage of a kind come from. A page with a
   *  hello catalog hands it in; `packRound` is the rows of the pack. */
  setRounds(rounds: (kind: number) => DetonationRound | undefined): void {
    this.rounds = rounds;
  }

  // -- what the feed fires ---------------------------------------------------

  explosion(x: number, y: number, faction: number, scale = 1): void {
    this.spawn('boom', x, y, faction, 1.5, scale, bubblesOf(10, 26, 14, 22));
  }

  ring(x: number, y: number, faction: number): void {
    this.spawn('ring', x, y, faction, 0.8);
  }

  /** A launch flash: brief, white, and it means someone just got loud. */
  launch(x: number, y: number, faction: number): void {
    this.spawn('launch', x, y, faction, 0.5);
  }

  hit(x: number, y: number, faction: number): void {
    this.spawn('hit', x, y, faction, 0.35);
  }

  /**
   * A round that went off (R2079): a torpedo hit, a fuel end or a rock
   * impact (R82: every round explodes). It draws the detonation look out to
   * the splash of `kind`, in the ink of the firer's faction. A kind of 0
   * draws the point form, inside the mark ring; a live round always names
   * its kind, and a round a guard or a burst took out draws the guard
   * look instead (R2080).
   *
   * `sx`, `sy` is the launch point of the round, the event's source. The
   * course from it to the impact is the forward of the screen and the
   * surge forms (R2842); a round with no launch point, or one that went
   * off where it left, takes no course.
   */
  detonate(x: number, y: number, faction: number, kind: number, sx = x, sy = y): void {
    const spec = kind > 0 ? this.rounds(kind) : undefined;
    const dx = x - sx, dy = y - sy;
    this.dets.push({
      x, y, age: 0, ink: factionColor(faction),
      R: Math.max(0, spec?.splash ?? 0), power: powerOf(spec?.damage ?? 0),
      form: formOf(kind), seed: seedOf(kind),
      course: dx * dx + dy * dy > COURSE_MIN_PX * COURSE_MIN_PX ? Math.atan2(dy, dx) : NaN,
    });
  }

  /**
   * The slow ring of a Torpor detonation (R2783): one thin ring that runs
   * out to `radius` world px, the reach in which the detonation slowed
   * every hostile hull and every hostile round (R2779 rule 2). It was the
   * ring of the round burst of R2018 until the Torpor stopped killing
   * rounds. The radius is a rule of the game, so the hold of R552 thins
   * the stroke and never shrinks the ring.
   */
  slowRing(x: number, y: number, faction: number, radius: number): void {
    this.spawn('slow', x, y, faction, 0.6, radius);
  }

  /** One effect of `life` seconds, in the ink of its faction. */
  private spawn(
    kind: FxKind, x: number, y: number, faction: number, life: number,
    scale = 1, bubbles: readonly Bubble[] = NO_BUBBLES,
  ): void {
    this.items.push({ kind, x, y, age: 0, life, color: factionColor(faction), scale, bubbles });
  }

  /** How many effects are alive. The tests and the stats line read it. */
  get count(): number { return this.items.length + this.dets.length; }

  /**
   * Takes every effect off the water now (R531).
   *
   * An effect normally lives out its age, because a blast that vanished
   * mid-flash would read as a fault. A replay film that seeks is the one
   * caller: the blast belongs to the time the film left, and it must not
   * burn on over the time it went to.
   */
  clear(): void { this.items.length = 0; this.dets.length = 0; }

  // -- the frame -------------------------------------------------------------

  update(frame: FrameInfo): void {
    this.advance(frame.dtMs / 1000);
    this.zoom = zoomOf(frame.cam.zoom);
    this.pack();
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
    this.segs.flush(ctx.cam);
    this.circles.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /** Ages every effect. The live ones close up in place, in their own
   *  order, so a death allocates nothing (§10.4). */
  private advance(dt: number): void {
    const items = this.items;
    let live = 0;
    for (let i = 0; i < items.length; i++) {
      const fx = items[i];
      fx.age += dt;
      if (fx.age <= fx.life) items[live++] = fx;
    }
    items.length = live;
    const dets = this.dets;
    live = 0;
    for (let i = 0; i < dets.length; i++) {
      const d = dets[i];
      d.age += dt;
      if (d.age <= DET_LIFE_SEC) dets[live++] = d;
    }
    dets.length = live;
  }

  // -- packing ---------------------------------------------------------------

  private pack(): void {
    const c = this.circles;
    // How much every length below shrinks this frame (R552).
    const k = holdScale(this.holdZoom, this.zoom);
    c.begin();
    this.quads.begin();
    this.segs.begin();
    this.packDetonations();
    for (const fx of this.items) {
      const t = fx.age / fx.life;
      switch (fx.kind) {
        case 'boom': this.packBoom(c, fx, t, k); break;
        case 'ring':
          c.ring(fx.x, fx.y, (6 + t * 32) * k, 2 * (1 - t) * k, fx.color, 0.8 * (1 - t));
          break;
        case 'hit':
          c.ring(fx.x, fx.y, (4 + t * 6) * k, 1.5 * k, fx.color, 0.5 * (1 - t));
          break;
        case 'launch':
          c.ring(fx.x, fx.y, (3 + t * 14) * k, 2 * (1 - t) * k, FLASH_WHITE, 0.55 * (1 - t));
          break;
        // `scale` holds the slow radius; the ring eases out to it.
        case 'slow':
          c.ring(fx.x, fx.y, fx.scale * (1 - (1 - t) * (1 - t)), 1.5 * k, fx.color, 0.6 * (1 - t));
          break;
      }
    }
  }

  /**
   * Every live detonation, through the look that draws now (R2079). The
   * splash is a rule of the game, so the hold of R552 never shrinks a look:
   * the card keeps its strokes at their screen width itself.
   */
  private packDetonations(): void {
    const c = this.det, look = this.look;
    c.row = look.row;
    c.z = this.zoom;
    for (const d of this.dets) {
      c.x = d.x; c.y = d.y; c.t = d.age;
      c.R = d.R; c.power = d.power; c.form = d.form; c.seed = d.seed; c.ink = d.ink; c.course = d.course;
      look.recipe(c);
    }
  }

  private packBoom(c: CircleSink, fx: Fx, t: number, k: number): void {
    const s = fx.scale * k;
    if (t < 0.22) c.disc(fx.x, fx.y, (10 * (1 - t / 0.22) + 4) * s, FLASH_WHITE, 0.9);
    if (t < 0.55) c.disc(fx.x, fx.y, (6 + 18 * (t / 0.55)) * s, FIREBALL, 0.65 * (1 - t / 0.55));
    c.ring(fx.x, fx.y, t * 56 * s, 2.5 * (1 - t) * s, fx.color, 0.7 * (1 - t));
    this.packBubbles(c, fx, t, 1.6, 3, 0.5, k);
  }

  /**
   * The bubbles rise and wobble on the effect's own age. The PixiJS layer
   * draws them as circles and then strokes them all at once, so each one is
   * a ring of `width · scale` and not a disc: `r` is that radius, `wobble`
   * the side swing and `alpha0` the ink at birth. The hold `k` shrinks the
   * spread and the rise with the rings, so the cloud stays on its blast.
   */
  private packBubbles(
    c: CircleSink, fx: Fx, t: number, r: number, wobble: number, alpha0: number, k: number,
  ): void {
    const s = fx.scale * k;
    const alpha = alpha0 * (1 - t);
    for (const b of fx.bubbles) {
      const y = fx.y - b.rise * fx.age * k;
      const x = fx.x + (b.dx + Math.sin(5 * fx.age + b.phase) * wobble) * k;
      c.ring(x, y, r * s, 1 * s, FOAM_INK, alpha);
    }
  }
}
